// PasswordInput — the kit TextInput with a trailing reveal toggle and an optional
// Caps Lock warning. Two properties are load-bearing:
//
//  1. The reveal SWAPS THE `type` ATTRIBUTE on one long-lived <input>; it never
//     swaps between two elements. Remounting would drop the caret position, the
//     composition state and (on some browsers) the value, and would blur the
//     field mid-typing — the classic broken "show password" eye.
//  2. The Caps Lock warning is a FILLED warning surface (spec §12: a status
//     surface never communicates with a border alone) inside a live region that
//     EXISTS BEFORE the warning does, so assistive tech announces the change
//     politely instead of missing a region that appeared with its content.
//
// Everything else — autocomplete, required, id, value, onInput, placeholder,
// aria-invalid, aria-describedby … — passes straight through to the input, so
// the component is a drop-in for <TextInput type="password">.
import { type JSX, Show, createSignal, createUniqueId, splitProps } from "solid-js";
import { Eye, EyeOff, TriangleAlert } from "lucide-solid";
import { TextInput } from "../field";
import { IconButton } from "../icon-button/index.js";

type InputKeyEvent = KeyboardEvent & {
  currentTarget: HTMLInputElement;
  target: Element;
};

/** Invoke a Solid event handler in either shape (function or [handler, data]). */
function callHandler(
  handler: JSX.EventHandlerUnion<HTMLInputElement, KeyboardEvent> | undefined,
  event: InputKeyEvent,
) {
  if (typeof handler === "function") handler(event);
  else if (handler) handler[0](handler[1], event);
}

export interface PasswordInputProps
  extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** id for the reveal toggle button. Defaults to `<input id>-reveal`. */
  toggleId?: string;
  /** Warn while Caps Lock is on. Defaults to on. */
  capsLockHint?: boolean;
  /** Class for the positioning wrapper — `class` still lands on the input. */
  wrapperClass?: string;
}

export function PasswordInput(props: PasswordInputProps) {
  const [local, rest] = splitProps(props, [
    "class",
    "wrapperClass",
    "toggleId",
    "capsLockHint",
    "id",
    "onKeyDown",
    "onKeyUp",
  ]);
  const [revealed, setRevealed] = createSignal(false);
  const [caps, setCaps] = createSignal(false);

  const fallbackId = createUniqueId();
  const inputId = () => local.id ?? `input-${fallbackId}`;
  const toggleId = () => local.toggleId ?? `${inputId()}-reveal`;
  const capsId = () => `${inputId()}-caps`;

  const readCapsLock = (event: InputKeyEvent) => {
    if (local.capsLockHint === false) return;
    // getModifierState is absent on synthetic events from some automation stacks.
    setCaps(typeof event.getModifierState === "function" && event.getModifierState("CapsLock"));
  };

  return (
    <div class={local.wrapperClass ?? "w-full"}>
      <div class="relative w-full">
        <TextInput
          {...rest}
          id={inputId()}
          type={revealed() ? "text" : "password"}
          class={`pr-12 ${local.class ?? ""}`}
          onKeyDown={(event) => {
            readCapsLock(event);
            callHandler(local.onKeyDown, event);
          }}
          onKeyUp={(event) => {
            readCapsLock(event);
            callHandler(local.onKeyUp, event);
          }}
        />
        {/* @erp/ui/icon-button — the 32px hit floor (40 at touch) around the
            16px field glyph. The input's pr-12 above clears it: right-2 + a
            40px touch box ends exactly 48px from the edge. A STATE toggle, so
            it keeps its tooltip: the label names what the press produces. */}
        <IconButton
          icon={revealed() ? EyeOff : Eye}
          label={revealed() ? "Hide password" : "Show password"}
          id={toggleId()}
          pressed={revealed()}
          controls={inputId()}
          class="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={() => setRevealed((shown) => !shown)}
        />
      </div>
      {/* The live region is always mounted so the warning is announced when it
          arrives, not silently inserted with a brand-new region. */}
      <div aria-live="polite" aria-atomic="true">
        <Show when={caps()}>
          {/* Two-tone: the orange-100 ground under BODY ink, the hue on the
              aria-hidden glyph only (DEV-GUIDE-002 §12.4, D4 — notice is a
              badge, a dot or an alert beside gray text, never the text). */}
          <p
            id={capsId()}
            class="mt-1 inline-flex items-center gap-1.5 rounded-field bg-warning-tint px-2 py-1 text-xs font-medium text-base-content"
          >
            <TriangleAlert size={13} class="text-warning" aria-hidden="true" />
            Caps Lock is on
          </p>
        </Show>
      </div>
    </div>
  );
}
